/**
 * Generic scene builders — capture the most common Remotion patterns
 * (title+subtext, title+list, title+number, etc.) so each scene file
 * stays ~5-15 LOC. These are visually less varied than the bespoke
 * ports, but they keep all 115 scenes in the SCENE_BUILDERS registry
 * so unported clips still render with appropriate brand styling
 * (instead of the labeled placeholder).
 */
import type { SceneBuilder } from "../reel/scenes.js";
import { brandBackdrop, type BackdropVariant } from "../primitives/brandBackdrop.js";
import {
  accentFor, clipFrame, clipOutroFade, combineSceneFragments, makeTl, typeSize,
  weights, withAlpha, colors as themeColors, escapeHtml, gsapSetLine, brandUrl,
} from "../reel/sceneShared.js";

export type GenericSceneConfig = {
  /** Backdrop variant. Default: "cinematic". */
  backdrop?: BackdropVariant;
  /** Title font size step. Default: "lg". */
  titleStep?: "md" | "lg" | "xl" | "hero" | "mega";
  /** Title text wraps; pass `transform: "uppercase" | "lowercase" | "none"`. */
  titleTransform?: "uppercase" | "lowercase" | "none";
  /** Override title alignment. Default: "center". */
  titleAlign?: "left" | "center" | "right";
  /** Subtext size step. Default: "md". */
  subStep?: "xs" | "sm" | "md" | "lg" | "xl";
  /** Show accent eyebrow above title (uses inputs.variant or constant). */
  showEyebrow?: boolean;
  /** Render items as a vertical list. */
  showItems?: boolean;
  /** Show big number from inputs.number (with optional unit). */
  showNumber?: boolean;
  /**
   * Show `inputs.url` (or brand fallback) as a pulsing CTA pill underneath
   * the rest of the content. Used by `genericCTA` so the user-entered URL
   * actually appears on screen instead of being silently dropped.
   */
  showUrl?: boolean;
  /** Animation flavor. */
  motion?: "fade" | "slide-up" | "slide-down" | "scale-in" | "split";
};

const STEP_MAP: Record<NonNullable<GenericSceneConfig["titleStep"]>, "lg" | "xl" | "hero" | "mega" | "md"> = {
  md: "md", lg: "lg", xl: "xl", hero: "hero", mega: "mega",
};

export function makeGenericScene(cfg: GenericSceneConfig = {}): SceneBuilder {
  const variant = cfg.backdrop ?? "cinematic";
  const titleStep = cfg.titleStep ?? "lg";
  const subStep = cfg.subStep ?? "md";
  const motion = cfg.motion ?? "slide-up";
  const titleAlign = cfg.titleAlign ?? "center";

  return ({ ctx, clipPrefix, inputs, clipDurationSec, clipStartSec }) => {
    const accent = accentFor(inputs);
    const text = inputs.text ?? "";
    const sub = inputs.subtext ?? "";
    const number = inputs.number;
    const unit = inputs.unit ?? "";
    const items = (inputs.items ?? []).slice(0, 6);

    const t = makeTl(clipStartSec);
    const titleId = `${clipPrefix}-title`;
    const subId = `${clipPrefix}-sub`;
    const numId = `${clipPrefix}-num`;
    const eyeId = `${clipPrefix}-eye`;
    const urlId = `${clipPrefix}-url`;

    const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant, accent });

    const titleSize = typeSize(STEP_MAP[titleStep], ctx.format);
    const subSize = typeSize(subStep, ctx.format);
    const megaSize = typeSize("mega", ctx.format);
    const transform = cfg.titleTransform && cfg.titleTransform !== "none" ? `text-transform:${cfg.titleTransform};` : "";

    const eyebrowHtml = cfg.showEyebrow
      ? `<div id="${eyeId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${typeSize("sm", ctx.format) * 0.7}px;letter-spacing:6px;text-transform:uppercase;color:${accent};">${escapeHtml(inputs.variant ?? "Allyver").toUpperCase()}</div>`
      : "";

    const numberHtml = cfg.showNumber && number
      ? `<div id="${numId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${megaSize * 0.9}px;line-height:.95;letter-spacing:-3.5px;color:${themeColors.fg};text-shadow:0 0 80px ${withAlpha(accent, 0.5)};font-variant-numeric:tabular-nums;">${escapeHtml(number)}${escapeHtml(unit)}</div>`
      : "";

    const titleHtml = text
      ? `<div id="${titleId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${titleSize}px;line-height:1.05;letter-spacing:-2px;color:${themeColors.fg};text-align:${titleAlign};text-wrap:balance;${transform}max-width:${ctx.format === "9:16" ? 940 : 1500}px;">${escapeHtml(text)}</div>`
      : "";

    const subHtml = sub
      ? `<div id="${subId}" style="font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:${subSize * 0.75}px;letter-spacing:-0.4px;line-height:1.3;color:${withAlpha(themeColors.fg, 0.85)};text-align:${titleAlign};max-width:${ctx.format === "9:16" ? 820 : 1200}px;">${escapeHtml(sub)}</div>`
      : "";

    // CTA-URL-Pill: leerer `inputs.url` fällt auf die Brand-URL zurück, sonst
    // wird genommen, was der Nutzer eingetragen hat. Damit ist garantiert,
    // dass die `ctaButton`-Szene immer eine sichtbare Adresse zeigt — vorher
    // wurde `inputs.url` in genericCTA komplett verschluckt (Bug aus dem
    // Remotion-Migrationsstand).
    const rawUrl = (inputs.url ?? "").trim();
    const fallbackUrl = String(brandUrl ?? "").replace(/^https?:\/\//i, "").replace(/\/$/, "");
    const urlText = rawUrl || fallbackUrl;
    const urlPillSize = typeSize("sm", ctx.format) * 0.92;
    const urlHtml = cfg.showUrl && urlText
      ? `<div id="${urlId}" style="display:inline-flex;align-items:center;gap:14px;margin-top:18px;padding:18px 36px;border-radius:9999px;background:linear-gradient(180deg, ${accent} 0%, ${withAlpha(accent, 0.85)} 100%);box-shadow:0 18px 50px ${withAlpha(accent, 0.55)}, inset 0 1px 0 ${withAlpha(themeColors.fg, 0.45)};font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${urlPillSize}px;letter-spacing:0.5px;color:${themeColors.bg};">
          <svg width="${urlPillSize * 1.05}" height="${urlPillSize * 1.05}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" style="opacity:0.9;"><circle cx="12" cy="12" r="10"/><path d="M2 12h20"/><path d="M12 2a15 15 0 0 1 0 20"/><path d="M12 2a15 15 0 0 0 0 20"/></svg>
          <span>${escapeHtml(urlText)}</span>
        </div>`
      : "";

    const itemsHtml = cfg.showItems && items.length
      ? `<div style="display:flex;flex-direction:column;gap:16px;width:100%;max-width:${ctx.format === "9:16" ? 900 : 1300}px;">${items
          .map(
            (it, i) =>
              `<div id="${clipPrefix}-it-${i}" style="display:flex;align-items:center;gap:24px;background:${withAlpha(themeColors.fg, 0.05)};border:1px solid ${withAlpha(themeColors.fg, 0.1)};border-radius:24px;padding:24px 40px;"><div style="width:48px;height:48px;min-width:48px;border-radius:9999px;background:${accent};display:flex;align-items:center;justify-content:center;font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:24px;color:${themeColors.bg};">${i + 1}</div><div style="font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:${subSize}px;color:${themeColors.fg};line-height:1.05;">${escapeHtml(it)}</div></div>`,
          )
          .join("")}</div>`
      : "";

    const inner = `${eyebrowHtml}${numberHtml}${titleHtml}${subHtml}${itemsHtml}${urlHtml}`;
    const html = clipFrame({ clipPrefix, clipStartSec, clipDurationSec, inner, gapPx: 40 });

    const initParts: string[] = [];
    const tweens: string[] = [];

    const animateIn = (id: string, atSec: number, prefersScale = false) => {
      if (motion === "fade") {
        initParts.push(gsapSetLine(`#${id}`, { opacity: 0 }));
        tweens.push(t.to(`#${id}`, { opacity: 1, duration: 0.6, ease: "raw:\"power2.out\"" }, atSec));
      } else if (motion === "slide-down") {
        initParts.push(gsapSetLine(`#${id}`, { opacity: 0, y: -32 }));
        tweens.push(t.to(`#${id}`, { opacity: 1, y: 0, duration: 0.73, ease: "raw:\"power2.out\"" }, atSec));
      } else if (motion === "scale-in" || prefersScale) {
        initParts.push(gsapSetLine(`#${id}`, { opacity: 0, scale: 0.85 }));
        tweens.push(t.to(`#${id}`, { opacity: 1, scale: 1, duration: 0.73, ease: "raw:\"back.out(1.4)\"" }, atSec));
      } else if (motion === "split") {
        initParts.push(gsapSetLine(`#${id}`, { opacity: 0, x: -60 }));
        tweens.push(t.to(`#${id}`, { opacity: 1, x: 0, duration: 0.73, ease: "raw:\"power2.out\"" }, atSec));
      } else {
        initParts.push(gsapSetLine(`#${id}`, { opacity: 0, y: 32 }));
        tweens.push(t.to(`#${id}`, { opacity: 1, y: 0, duration: 0.73, ease: "raw:\"power2.out\"" }, atSec));
      }
    };

    let at = 0;
    if (cfg.showEyebrow) {
      animateIn(eyeId, at);
      at += 0.15;
    }
    if (cfg.showNumber && number) {
      animateIn(numId, at, true);
      at += 0.25;
    }
    if (text) {
      animateIn(titleId, at);
      at += 0.2;
    }
    if (sub) {
      animateIn(subId, at);
      at += 0.15;
    }
    if (cfg.showItems && items.length) {
      items.forEach((_, i) => animateIn(`${clipPrefix}-it-${i}`, at + i * (7 / 30)));
      at += items.length * (7 / 30);
    }
    if (cfg.showUrl && urlText) {
      // Pop-in mit Back-Ease + sanfter Dauer-Puls, damit die CTA-Pille
      // wirkt wie ein Knopf, den man drücken möchte.
      initParts.push(gsapSetLine(`#${urlId}`, { opacity: 0, scale: 0.85, y: 10 }));
      tweens.push(t.to(`#${urlId}`, { opacity: 1, scale: 1, y: 0, duration: 0.6, ease: "raw:\"back.out(2.0)\"" }, at));
      tweens.push(t.to(`#${urlId}`, { scale: 1.04, duration: 0.6, repeat: -1, yoyo: true, ease: "raw:\"sine.inOut\"" }, at + 0.7));
    }

    tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec));

    return combineSceneFragments([
      backdrop,
      { html, init: initParts.join("\n"), tweens },
    ]);
  };
}

/**
 * Pre-baked variants for the most common scene archetypes.
 */
export const genericTitle = makeGenericScene({ backdrop: "cinematic", titleStep: "lg" });
export const genericHero = makeGenericScene({ backdrop: "cinematic", titleStep: "hero", motion: "scale-in" });
export const genericList = makeGenericScene({ backdrop: "allyver", titleStep: "lg", showItems: true });
export const genericNumber = makeGenericScene({ backdrop: "cinematic", titleStep: "md", showNumber: true });
export const genericPartner = makeGenericScene({ backdrop: "allyver", titleStep: "xl", showEyebrow: true });
export const genericCTA = makeGenericScene({ backdrop: "cinematic", titleStep: "xl", motion: "scale-in", showUrl: true });
export const genericData = makeGenericScene({ backdrop: "allyver", titleStep: "lg", showNumber: true });
export const genericIntro = makeGenericScene({ backdrop: "gradient", titleStep: "hero", motion: "scale-in" });
export const genericOutro = makeGenericScene({ backdrop: "cinematic", titleStep: "lg", motion: "fade" });
